import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Modal,
  ModalDialog,
  ModalClose,
} from "@mui/joy";
import {
  FloppyDisk as Save,
  ArrowLeft,
  UploadSimple as Upload,
  FileText,
  XCircle as AlertCircle,
  Files as Pages,
  Layout,
} from "@phosphor-icons/react";
import { PdfFieldDesigner } from "../PdfFieldDesigner";
import { PdfPageManager, type PdfPageChange } from "./PdfPageManager";
import { api, fetchApiBlob } from "../../../lib/api";

import "../../../lib/pdfWorkerSetup";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import type { ContractField } from "../../../types";

interface TemplateDesignerProps {
  templateId: number;
  onSave?: () => void;
  onCancel?: () => void;
}

export const TemplateDesigner: React.FC<TemplateDesignerProps> = ({
  templateId,
  onSave,
  onCancel,
}) => {
  const [template, setTemplate] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<ContractField[]>([]);
  const [showFieldDesigner, setShowFieldDesigner] = useState(false);
  const [selectedFieldType, setSelectedFieldType] = useState<string | null>(
    null,
  );
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [pdfData, setPdfData] = useState<Uint8Array | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  // pdf_url is a fixed proxy path, so a replaced PDF leaves it unchanged and
  // neither this effect nor the browser cache would notice the new file.
  const [pdfVersion, setPdfVersion] = useState(0);
  const [numPages, setNumPages] = useState<number>(0);
  const [showPageManager, setShowPageManager] = useState(false);

  useEffect(() => {
    loadTemplate();
  }, [templateId]);

  // Fetch PDF with authentication when template has a PDF
  useEffect(() => {
    const fetchPdfWithAuth = async () => {
      if (!template?.pdf_url) {
        setPdfData(null);
        setNumPages(0);
        return;
      }

      try {
        setPdfLoading(true);

        const blob = await fetchApiBlob(
          `/api/crm/contract-templates/${templateId}/view_pdf/?v=${pdfVersion}`,
        );
        const arrayBuffer = await blob.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        setPdfData(uint8Array);
      } catch (err) {
        console.error("Failed to fetch PDF:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load PDF document",
        );
      } finally {
        setPdfLoading(false);
      }
    };

    fetchPdfWithAuth();
  }, [template?.pdf_url, templateId, pdfVersion]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const response = await api.get<any>(
        `/api/crm/contract-templates/${templateId}/`,
      );
      setTemplate(response.data);
      setFields(response.data?.template_fields || []);
    } catch (error) {
      setError("Failed to load template");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadPDF = async (file: File) => {
    try {
      const response = await api.uploadContractTemplatePdf(templateId, file);

      if (response.status === 200) {
        await loadTemplate();
        setPdfVersion((version) => version + 1);
        setError(null);
        // Automatically open the field designer after successful upload
        setShowFieldDesigner(true);
      } else {
        setError(response.data?.error || "Failed to upload PDF");
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : "Failed to upload PDF");
    }
  };

  const handleSaveTemplate = async () => {
    try {
      setSaving(true);
      await api.patch(`/api/crm/contract-templates/${templateId}/`, {
        template_fields: fields,
        pdf_pages: template.pdf_pages,
      });

      if (onSave) {
        onSave();
      }
      setError(null);
    } catch (error) {
      setError("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handlePagesChanged = useCallback(
    ({ pageInfo, fields: renumbered }: PdfPageChange) => {
      // The server has already persisted both halves; adopting its field list is
      // what keeps a field placed on page 4 off page 3 after a page is deleted.
      setFields(renumbered);
      setTemplate((prev: any) =>
        prev ? { ...prev, pdf_pages: pageInfo } : prev,
      );
      setSelectedFieldId(null);
      setPdfVersion((version) => version + 1);
      setError(null);
    },
    [],
  );

  const handleFieldAdd = useCallback(
    (field: Omit<ContractField, "id">) => {
      const newField: ContractField = {
        ...field,
        id: crypto.randomUUID(),
      };
      setFields([...fields, newField]);
      setSelectedFieldId(newField.id);
    },
    [fields],
  );

  const handleFieldUpdate = useCallback(
    (fieldId: string, updates: Partial<ContractField>) => {
      setFields(
        fields.map((f) => (f.id === fieldId ? { ...f, ...updates } : f)),
      );
    },
    [fields],
  );

  const handleFieldDelete = useCallback(
    (fieldId: string) => {
      setFields(fields.filter((f) => f.id !== fieldId));
    },
    [fields],
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Clear the input so picking the same filename again still fires onChange -
    // re-uploading a corrected copy under the same name is the common case.
    e.target.value = "";
    if (file) {
      handleUploadPDF(file);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!template) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger">Failed to load template</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Card variant="outlined">
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography level="title-lg">{template.name}</Typography>
              <Typography level="body-sm" textColor="text.secondary">
                Template ID: {templateId} • {fields.length} fields
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="primary"
                startDecorator={<Save size={16} />}
                onClick={handleSaveTemplate}
                loading={saving}
              >
                Save Template
              </Button>
              {onCancel && (
                <Button
                  variant="plain"
                  onClick={onCancel}
                  startDecorator={<ArrowLeft size={16} />}
                >
                  Back
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert color="danger" sx={{ mx: 2, mt: 2 }}>
          <AlertCircle size={16} />
          {error}
        </Alert>
      )}

      {/* Main Content */}
      <Box sx={{ flex: 1, display: "flex", p: 2, gap: 2, overflow: "hidden" }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {template.pdf_url ? (
            <Card variant="outlined" sx={{ flex: 1 }}>
              <CardContent
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography level="title-md">
                    PDF Document
                    {numPages > 0
                      ? ` • ${numPages} page${numPages === 1 ? "" : "s"}`
                      : ""}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      color="neutral"
                      size="sm"
                      startDecorator={<Pages size={16} />}
                      onClick={() => setShowPageManager(true)}
                    >
                      Manage Pages
                    </Button>
                    <Button
                      variant="outlined"
                      color="neutral"
                      size="sm"
                      component="label"
                      startDecorator={<Upload size={16} />}
                    >
                      Replace PDF
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleFileSelect}
                        style={{ display: "none" }}
                      />
                    </Button>
                    <Button
                      variant="solid"
                      color="primary"
                      size="sm"
                      startDecorator={<Layout size={16} />}
                      onClick={() => setShowFieldDesigner(true)}
                    >
                      Design Fields
                    </Button>
                  </Stack>
                </Box>

                {/* PDF Preview */}
                <Box
                  sx={{
                    flex: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "sm",
                    bgcolor: "background.level2",
                    overflow: "auto",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    p: 2,
                  }}
                >
                  {pdfLoading ? (
                    <CircularProgress />
                  ) : pdfData ? (
                    <Document
                      file={{ data: new Uint8Array(pdfData) }}
                      onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                      loading={<CircularProgress />}
                    >
                      {Array.from({ length: numPages }, (_, i) => (
                        <Box key={i + 1} sx={{ mb: 2, boxShadow: "sm" }}>
                          <Page
                            pageNumber={i + 1}
                            width={500}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                          />
                        </Box>
                      ))}
                    </Document>
                  ) : (
                    <Typography level="body-sm" textColor="text.secondary">
                      PDF loading...
                    </Typography>
                  )}
                </Box>

                {/* Field Summary */}
                {fields.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography level="body-sm" sx={{ mb: 1 }}>
                      Template Fields ({fields.length})
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {fields.map((field, index) => (
                        <Box
                          key={field.id}
                          sx={{
                            px: 1,
                            py: 0.5,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: "xs",
                            fontSize: "xs",
                          }}
                        >
                          {field.type}: {field.label || `Field ${index + 1}`}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card
              variant="outlined"
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CardContent sx={{ textAlign: "center", maxWidth: 500 }}>
                <Stack spacing={2} alignItems="center">
                  <FileText size={48} color="var(--joy-palette-neutral-400)" />
                  <Typography level="title-md">No PDF Uploaded</Typography>
                  <Typography level="body-sm" textColor="text.secondary">
                    Upload a PDF document to start designing your template
                  </Typography>
                  <Button
                    variant="solid"
                    component="label"
                    startDecorator={<Upload size={16} />}
                  >
                    Upload PDF
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleFileSelect}
                      style={{ display: "none" }}
                    />
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>

      {/* Field Designer Modal */}
      {showFieldDesigner && template.pdf_url && (
        <Modal
          open={showFieldDesigner}
          onClose={() => setShowFieldDesigner(false)}
        >
          <ModalDialog
            size="lg"
            sx={{ maxWidth: "95vw", maxHeight: "95vh", width: "95vw" }}
          >
            <ModalClose />
            <Typography level="h4" sx={{ mb: 2 }}>
              Design Template Fields: {template.name}
            </Typography>

            <Alert color="primary" sx={{ mb: 2 }}>
              <Typography level="body-sm">
                Click on the PDF to add fillable fields. Use the field palette
                on the right to select field types.
              </Typography>
            </Alert>

            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, height: "75vh", minHeight: 0, gap: 2 }}>
              {/* PDF Field Designer */}
              <Box
                sx={{
                  flex: 1, minWidth: 0, minHeight: 0,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "sm",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {pdfLoading ? (
                  <CircularProgress />
                ) : pdfData ? (
                  <PdfFieldDesigner
                    pdfData={pdfData}
                    contractId={templateId}
                    existingFields={fields}
                    selectedFieldType={selectedFieldType || undefined}
                    onFieldAdded={handleFieldAdd}
                    onFieldUpdated={(field) =>
                      handleFieldUpdate(field.id, field)
                    }
                    onFieldDeleted={handleFieldDelete}
                  />
                ) : (
                  <Typography level="body-sm" textColor="text.secondary">
                    Loading PDF for field designer...
                  </Typography>
                )}
              </Box>

              {/* Field Palette */}
              <Box
                sx={{
                  width: { xs: "100%", md: "210px" }, flexShrink: 0,
                  overflowY: "auto", maxHeight: { xs: 180, md: "none" },
                  borderLeft: "1px solid",
                  borderColor: "divider",
                  pl: 2,
                }}
              >
                <Typography level="title-md" sx={{ mb: 2 }}>
                  Field Types
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Button
                    variant={
                      selectedFieldType === "text" ? "solid" : "outlined"
                    }
                    size="sm"
                    sx={{ justifyContent: "flex-start" }}
                    onClick={() =>
                      setSelectedFieldType((prev) =>
                        prev === "text" ? null : "text",
                      )
                    }
                  >
                    📝 Text Field
                  </Button>
                  <Button
                    variant={
                      selectedFieldType === "signature" ? "solid" : "outlined"
                    }
                    size="sm"
                    sx={{ justifyContent: "flex-start" }}
                    onClick={() =>
                      setSelectedFieldType((prev) =>
                        prev === "signature" ? null : "signature",
                      )
                    }
                  >
                    ✍️ Signature
                  </Button>
                  <Button
                    variant={
                      selectedFieldType === "date" ? "solid" : "outlined"
                    }
                    size="sm"
                    sx={{ justifyContent: "flex-start" }}
                    onClick={() =>
                      setSelectedFieldType((prev) =>
                        prev === "date" ? null : "date",
                      )
                    }
                  >
                    📅 Date
                  </Button>
                  <Button
                    variant={
                      selectedFieldType === "checkbox" ? "solid" : "outlined"
                    }
                    size="sm"
                    sx={{ justifyContent: "flex-start" }}
                    onClick={() =>
                      setSelectedFieldType((prev) =>
                        prev === "checkbox" ? null : "checkbox",
                      )
                    }
                  >
                    ☑️ Checkbox
                  </Button>
                  <Button
                    variant={
                      selectedFieldType === "initial" ? "solid" : "outlined"
                    }
                    size="sm"
                    sx={{ justifyContent: "flex-start" }}
                    onClick={() =>
                      setSelectedFieldType((prev) =>
                        prev === "initial" ? null : "initial",
                      )
                    }
                  >
                    ✏️ Initial
                  </Button>
                </Box>

                <Box
                  sx={{
                    mt: 3,
                    pt: 2,
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                    <strong>Instructions:</strong>
                    <br />
                    1. Select a field type above
                    <br />
                    2. Click on the PDF where you want to place the field
                    <br />
                    3. Drag fields to reposition them
                    <br />
                    4. Use resize handles to adjust size
                    <br />
                    5. Click a field to edit its properties
                  </Typography>

                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="solid"
                      color="primary"
                      size="sm"
                      onClick={() => {
                        setShowFieldDesigner(false);
                        handleSaveTemplate();
                      }}
                    >
                      Save & Close
                    </Button>
                    <Button
                      variant="outlined"
                      size="sm"
                      onClick={() => setShowFieldDesigner(false)}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          </ModalDialog>
        </Modal>
      )}

      {/* Status Bar */}
      <Card variant="outlined">
        <CardContent sx={{ py: 1 }}>
          <Stack direction="row" spacing={4} alignItems="center">
            <Typography level="body-xs" textColor="text.secondary">
              Template Fields: {fields.length}
            </Typography>

            {template.pdf_pages?.length > 0 && (
              <Typography level="body-xs" textColor="text.secondary">
                Pages: {template.pdf_pages.length}
              </Typography>
            )}

            {template.is_active && (
              <Typography level="body-xs" color="success">
                • Active
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Page Manager */}
      {template.pdf_url && (
        <PdfPageManager
          open={showPageManager}
          onClose={() => setShowPageManager(false)}
          pdfUrl={`/api/crm/contract-templates/${templateId}/view_pdf/`}
          documentId={templateId}
          isTemplate
          title={`Manage Pages — ${template.name}`}
          onPagesChanged={handlePagesChanged}
        />
      )}
    </Box>
  );
};
